import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Field, reduxForm, change } from 'redux-form';
import { useIntl, FormattedMessage } from 'react-intl';
import withStyles from 'isomorphic-style-loader/withStyles';
import cx from 'classnames';
import {
  Button,
  FormGroup,
  Col,
  Row,
  Panel,
} from 'react-bootstrap';

import { url } from '../../../config.js';
import { formatURL } from '../../../helpers/formatURL';
import messages from '../../../locale/messages';
import submit from './submit';
import validate from './validate';
import Link from '../../Link';
import InputFieldComponent from '../../Common/FormField/InputFieldComponent';
import SelectFieldComponent from '../../Common/FormField/SelectFieldComponent';

import s from './EditBlogDetails.css';
import bt from '../../../components/commonStyle.css';

const RenderQuill = ({ input, meta: { touched, error }, placeholder }) => {
  const intl = useIntl();
  const [Editor, setEditor] = useState(null);
  useEffect(() => {
    if (typeof window !== 'undefined') {
      import('../../Common/Editor/CustomEditor').then(mod => setEditor(() => mod.Editor));
    }
  }, []);
  return (
    Editor &&
    <div>
      <Editor
        {...input}
        placeholder={placeholder}
      />
      {touched && error && <span className={s.errorMessage}>{intl.formatMessage(error)}</span>}
    </div>
  )
}

function EditBlogDetails({ handleSubmit, submitting, error }) {
  const intl = useIntl();
  const dispatch = useDispatch();
  const [Editor, setEditor] = useState(null);

  const siteName = useSelector(state => state.siteSettings.data.siteName);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      import('../../Common/Editor/CustomEditor').then(mod => setEditor(() => mod.Editor));
    }
  }, []);

  const handlePageTitle = async (e) => {
    const value = e.target.value;
    await dispatch(change('BlogDetails', 'pageUrl', value ? formatURL(value) : ''));
  };

  return (
    <div>
      <h1 className={s.siteAdminHeading}><FormattedMessage {...messages.editPageDetails} /></h1>
      <Row>
        <Col xs={12} sm={12} md={12} lg={8} className={s.blockCenter}>
          <div className={cx(s.space4, 'singleButtonFlexEnd')}>
            <Link to="/siteadmin/content-management" className={cx(bt.btnPrimaryBorder, bt.btnLarge, bt.noTextDecoration)}>
              <FormattedMessage {...messages.goBack} />
            </Link>
          </div>
          <Panel className={cx(s.panelHeader)}>
            <form onSubmit={handleSubmit(submit)}>
              {error && <strong>{intl.formatMessage(error)}</strong>}

              <Field
                name="metaTitle"
                type="text"
                component={InputFieldComponent}
                label={intl.formatMessage(messages.metaTitleLabel)}
                placeholder={intl.formatMessage(messages.metaTitlePlaceHolder)}
                inputClass={bt.commonControlInput}
                formGroupClass={bt.space4}
              />

              <Field
                name="metaDescription"
                inputClass={bt.commonControlTextArea}
                formGroupClass={bt.space4}
                component={InputFieldComponent}
                componentClass="textarea"
                label={intl.formatMessage(messages.metaDescriptionLabel)}
                placeholder={intl.formatMessage(messages.metaDescriptionPlaceHolder)}
              />

              <Field
                name="pageTitle"
                type="text"
                component={InputFieldComponent}
                label={intl.formatMessage(messages.pageTitleLabel)}
                placeholder={intl.formatMessage(messages.pageTitlePlaceHolder)}
                onChange={handlePageTitle}
                inputClass={bt.commonControlInput}
                formGroupClass={bt.space4}
              />

              <Field
                name="pageUrl"
                type="text"
                component={InputFieldComponent}
                isAddon
                suffixLabel={`${url}${intl.formatMessage(messages.pageLabel)}`}
                label={intl.formatMessage(messages.pageUrl)}
                placeholder={intl.formatMessage(messages.pageUrlExample)}
                inputClass={cx(bt.commonControlInput, 'staticPageUrlInput')}
                formGroupClass={cx("staticPageUrl", bt.space4)}
              />

              <Field
                name="footerCategory"
                inputClass={cx(bt.commonControlSelect, 'commonControlSelectRtl')}
                formGroupClass={bt.space4}
                component={SelectFieldComponent}
                label={intl.formatMessage(messages.footerCategoryLabel)}
                options={[
                  { value: '', label: intl.formatMessage(messages.ChooseFooterCategory) },
                  { value: siteName, label: siteName },
                  { value: 'discover', label: intl.formatMessage(messages.discover) },
                  { value: 'hosting', label: intl.formatMessage(messages.hosting) }
                ]}
              />

              <FormGroup className={bt.space4}>
                <label className={s.labelTextNew}><FormattedMessage {...messages.contentLabel} /></label>
                <Field name="content" component={RenderQuill} placeholder={intl.formatMessage(messages.contentPlaceHolder)} />
              </FormGroup>

              <div className='singleButtonFlexEnd'>
                <Button className={cx(bt.btnPrimary, bt.btnLarge)} type="submit" disabled={submitting}>
                  <FormattedMessage {...messages.save} />
                </Button>
              </div>
            </form>
          </Panel>
        </Col>
      </Row>
    </div>
  );
}

EditBlogDetails.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  submitting: PropTypes.bool,
  error: PropTypes.any,
};

export default reduxForm({
  form: 'BlogDetails',
  validate
})(withStyles(s, bt)(EditBlogDetails));